
import api from './api';
import mockApi from './mockApi';
import { AxiosInstance } from 'axios';
import { API_CONFIG } from '../config/apiConfig';
import uploadService from './upload.service';
import { 
  BasicResponse, 
  ExternalCandidateSearchParams, 
  ExternalCandidateSearchResponse,
  Attachments 
} from '../types/api';
import apiLocal from './api_local';

// const USE_MOCK_API =
//   process.env.REACT_APP_USE_MOCK === "true" && API_CONFIG.IS_DEVELOPMENT;
// const apiClient = USE_MOCK_API ? (mockApi as any) : (api as AxiosInstance);
// const apiClient = apiLocal as AxiosInstance; // TODO revert to api after testing
const apiClient = api as AxiosInstance; // TODO revert to api after testing

const externalCandidatesService = {
  /**
   * Search external candidates with various filters
   * @param params - Search parameters including pageNumber, pageSize, cityId, skills, etc.
   * @returns Promise with search results and pagination info
   */
  searchExternalCandidates: async (
    params: ExternalCandidateSearchParams
  ): Promise<BasicResponse<ExternalCandidateSearchResponse>> => {
    try {
      const response = await apiClient.get('/external-candidates', { params });
      return response.data;
    } catch (error: any) {
      console.error('Search external candidates error:', error);
      throw error.response?.data || error.message;
    }
  },

  /**
   * Migrate a resume from an external URL to internal storage
   * Downloads the resume from the external URL, uploads it to internal storage,
   * confirms the upload, and returns the attachment details.
   * 
   * @param params - Object containing externalUrl and candidateName
   * @returns Promise with the attachment object
   */
  migrateResume: async (
    params: { externalUrl: string; candidateName: string }
  ): Promise<Attachments> => {
    const { externalUrl, candidateName } = params;

    try {
      // Step 1: Fetch the external resume as a blob
      const fetchResponse = await fetch(externalUrl);
      if (!fetchResponse.ok) {
        throw new Error(`Failed to fetch resume from external URL: ${fetchResponse.statusText}`);
      }

      const blob = await fetchResponse.blob();
      
      // Determine file extension from content-type or default to pdf
      const contentType = blob.type || 'application/pdf';
      const extension = contentType === 'application/pdf' ? '.pdf' : 
                       contentType === 'application/msword' ? '.doc' :
                       contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ? '.docx' : '.pdf';
      
      const fileName = `${candidateName}${extension}`;

      // Step 2: Get upload URL from the upload service
      const uploadUrlResponse = await uploadService.getUploadUrl({
        fileName,
        contentType,
        type: 'Resume',
        folder: 'SurveyData',
      });

      if (!uploadUrlResponse.data || !uploadUrlResponse.data.preSignedUrl) {
        throw new Error('Failed to get upload URL');
      }

      const { preSignedUrl, attachmentId } = uploadUrlResponse.data;

      // Step 3: Upload file to the pre-signed URL using fetch
      const uploadResponse = await fetch(preSignedUrl, {
        method: 'PUT',
        body: blob,
        headers: {
          'Content-Type': contentType,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
      }

      // Step 4: Confirm the upload
      await uploadService.confirmUpload(attachmentId);

      // Step 5: Get the full attachment details
      const attachmentResponse = await uploadService.getAttachment(attachmentId);

      if (!attachmentResponse.data) {
        throw new Error('Failed to retrieve attachment details');
      }

      return attachmentResponse.data;
    } catch (error: any) {
      console.error('Migrate resume error:', error);
      throw error.response?.data || error.message || error;
    }
  },
};

export default externalCandidatesService;
